import Joi from 'joi'

const states: { [key: string]: string } = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  'District of Columbia': 'DC',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
  'Armed Forces of the Americas': 'AA',
  'Armed Forces of Europe': 'AE',
  'Armed Forces of the Pacific': 'AP',
}

const abbreviations: { [key: string]: string } = Object.entries(states).reduce<{
  [key: string]: string
}>((acc, [name, abbr]) => {
  acc[abbr] = name
  return acc
}, {})

export function toAbbreviation(stateName: string): string | null {
  return states[stateName.trim()] || null
}

export function toStateName(abbreviation: string): string | null {
  return abbreviations[abbreviation.trim().toUpperCase()] || null
}

export const stateDropdownItems = Object.entries(states).map(([label, value]) => ({ label, value }))

const zipCodeFieldsSchema = {
  zipCode: Joi.string()
    .pattern(/^\d{5}(-\d{4})?$/)
    .required()
    .messages({
      'string.empty': 'Zip Code is required',
      'string.pattern.base':
        'Zip Code must be either 5 digits or 5 digits followed by a hyphen and 4 digits',
      'any.required': 'Zip Code is a required field',
    }),
  state: Joi.string()
    .required()
    .messages({ 'string.empty': 'State is required', 'any.required': 'State is a required field' }),
  city: Joi.string()
    .pattern(/^['\p{L}]+(?:[ \p{L},'-:])*$/u)
    .required()
    .messages({
      'string.empty': 'City is required',
      'string.pattern.base': 'City must match the required pattern',
      'any.required': 'City is a required field',
    }),
}

export const zipCodeSchema = Joi.object({
  street1: Joi.string().allow('', null),
  street2: Joi.string().allow('', null),
  ...zipCodeFieldsSchema,
})

/**
 * @see {@link https://highnote.com/docs/reference/input_object/AddressInput}
 * When the field is used for creating an application, it will also validate that a PO Box is not included using this regex pattern
 */
const cardApplicationAddressPattern =
  /^(?:((\S([^pPOo])+)|(?:[0-9]+)))\s(?:[0-9A-Za-z.]|[^\S\r\n])+$/
export const cardApplicationAddressSchema = Joi.object({
  street1: Joi.string().pattern(cardApplicationAddressPattern).required().messages({
    'string.empty': 'Street 1 is a required field',
    'string.pattern.base': 'Street 1 is not a valid address or cannot be a PO Box',
    'any.required': 'Street 1 is a required field',
  }),
  street2: Joi.string()
    .pattern(/^[a-zA-Z\d',. \-#]+(([',. \-#][a-zA-Z ])?[a-zA-Z.]*)*$/)
    .allow('', null)
    .messages({ 'string.pattern.base': 'Street 2 does not match the required pattern' }),
  ...zipCodeFieldsSchema,
})

/**
 * @see {@link https://highnote.com/docs/reference/input_object/AddressInput}
 * When the field is used for creating a physical payment card order or physical payment card group order, it will also support mailing to a PO Box using this regex pattern
 */
const cardPhysicalShipAddressPattern =
  /^(?:(?:(?:\S[^pPOo\r\n]+)|(?:[0-9/#]+)))\s(?:[0-9A-Za-z.]|[^\S\r\n])+$|^\b[P|p]*(?:OST|ost)*\.*\s*[O|o|0]*(?:ffice|FFICE)*\.*\s*[B|b][O|o|0][X|x]\s[0-9A-Za-z]+\b$/
export const cardPhysicalShipAddressSchema = Joi.object({
  street1: Joi.string().pattern(cardPhysicalShipAddressPattern).required().messages({
    'string.empty': 'Street 1 is a required field',
    'string.pattern.base': 'Street 1 is not a valid address',
    'any.required': 'Street 1 is a required field',
  }),
  street2: Joi.string()
    .pattern(/^[a-zA-Z\d',. \-#]+(([',. \-#][a-zA-Z ])?[a-zA-Z.]*)*$/)
    .allow('', null)
    .messages({ 'string.pattern.base': 'Street 2 does not match the required pattern' }),
  ...zipCodeFieldsSchema,
})

export interface Address {
  street1: string
  street2: string
  city: string
  state: string
  zipCode: string
}

export const emptyAddress = () => ({ street1: '', street2: '', city: '', state: '', zipCode: '' })

export interface AddressError {
  street1: string | undefined
  street2: string | undefined
  city: string | undefined
  state: string | undefined
  zipCode: string | undefined
}

export const mapErrors = (joiError: Joi.ValidationError) =>
  joiError.details.reduce<Record<string, string>>((acc, err) => {
    // @ts-expect-error this code was written before we started using `strictNullChecks` and `noUncheckedIndexedAccess` but we didn't fix this here to derisk the transition. we should fix this eventually
    acc[err.path[0]] = err.message
    return acc
  }, {})

export type AutoCompleteSearchMode =
  | 'full-address-unvalidated'
  | 'zip-code'
  | 'card-application-address'
  | 'card-physical-ship-address'

export interface AddressInputsProps {
  address: Address
  onChange: (address: Address | undefined) => void
  showValidationErrors: boolean
  validationErrors: AddressError | undefined
  testIDPrefix?: string
}
